import React, { useCallback, useEffect, useRef, useState } from "react";

import { useTranslation } from "react-i18next";

import "./FeatureCarousel.css";

/* =========================================================
   DATA
   ========================================================= */

const carouselSections = [
  /* =======================================================
     PLANNING
     ======================================================= */

  {
    id: "planning",
    premium: false,

    label: {
      de: "Planung",
      en: "Planning",
    },

    eyebrow: {
      de: "Planung",
      en: "Planning",
    },

    features: [
      {
        id: "planning-overview",

        title: {
          de: "Gemeinsam planen. Alles im Blick.",
          en: "Plan together. Stay organized.",
        },

        description: {
          de: "Termine, Stundenpläne und gemeinsame Entscheidungen – mit oneNizer organisiert ihr euren Alltag übersichtlich an einem Ort.",

          en: "Appointments, schedules and shared decisions – oneNizer helps you organize everyday life clearly in one place.",
        },

        highlights: {
          de: [
            "Termine & Ereignisse übersichtlich planen",
            "Stundenpläne gemeinsam organisieren",
            "Mit Abstimmungen schneller entscheiden",
          ],

          en: [
            "Plan appointments & events clearly",
            "Organize schedules together",
            "Make decisions faster with polls",
          ],
        },

        /*
         * Eigenes Planning-Layout:
         * 3 Geräte in versetzter Timeline.
         */
        visualType: "planning-flow",

        images: [
          {
            src: {
              de: "/images/showcase/planning/schedule-day-en.jpg",
              en: "/images/showcase/planning/schedule-day-en.jpg",
            },

            fallback: "/images/showcase/planning/schedule-day.jpg",

            alt: {
              de: "oneNizer Stundenplan Tagesansicht",
              en: "oneNizer schedule day view",
            },
          },

          {
            src: {
              de: "/images/showcase/planning/calendar-month-en.jpg",
              en: "/images/showcase/planning/calendar-month-en.jpg",
            },

            fallback: "/images/showcase/planning/calendar-month.jpg",

            alt: {
              de: "oneNizer Kalender Monatsansicht",
              en: "oneNizer calendar month view",
            },
          },

          {
            src: {
              de: "/images/showcase/planning/poll-en.jpg",
              en: "/images/showcase/planning/poll-en.jpg",
            },

            fallback: "/images/showcase/planning/poll.jpg",

            alt: {
              de: "oneNizer Abstimmungen",
              en: "oneNizer polls",
            },
          },
        ],
      },
    ],
  },

  /* =======================================================
     LISTS
     ======================================================= */

  {
    id: "lists",
    premium: false,

    label: {
      de: "Listen",
      en: "Lists",
    },

    eyebrow: {
      de: "Listen",
      en: "Lists",
    },

    features: [
      {
        id: "lists-overview",

        title: {
          de: "Listen, die mit euch mitdenken",
          en: "Lists that keep everything organized",
        },

        description: {
          de: "Von Aufgaben und Notizen bis zur gemeinsamen Einkaufsliste – oneNizer bringt eure wichtigsten Listen übersichtlich an einen Ort.",

          en: "From tasks and notes to shared shopping lists – oneNizer keeps your most important lists organized in one place.",
        },

        highlights: {
          de: [
            "Aufgaben gemeinsam organisieren",
            "Notizen & Ideen jederzeit festhalten",
            "Einkäufe planen & Fortschritt verfolgen",
          ],

          en: [
            "Organize tasks together",
            "Capture notes & ideas anytime",
            "Plan shopping & track progress",
          ],
        },

        /*
         * Eigene Darstellung für Lists:
         * ein großes Phone links,
         * zwei gestaffelte Phones rechts.
         */
        visualType: "lists-stack",

        images: [
          {
            src: {
              de: "/images/showcase/lists/todo-en.jpg",
              en: "/images/showcase/lists/todo-en.jpg",
            },

            fallback: "/images/showcase/lists/todo.jpg",

            alt: {
              de: "oneNizer To-Do Übersicht",
              en: "oneNizer To-Do overview",
            },
          },

          {
            src: {
              de: "/images/showcase/lists/note-en.jpg",
              en: "/images/showcase/lists/note-en.jpg",
            },

            fallback: "/images/showcase/lists/note.jpg",

            alt: {
              de: "oneNizer Notizen Übersicht",
              en: "oneNizer Notes overview",
            },
          },

          {
            src: {
              de: "/images/showcase/lists/shopping-en.jpg",
              en: "/images/showcase/lists/shopping-en.jpg",
            },

            fallback: "/images/showcase/lists/shopping.jpg",

            alt: {
              de: "oneNizer Einkaufslisten Übersicht",
              en: "oneNizer Shopping list overview",
            },
          },
        ],
      },
    ],
  },

  /* =======================================================
     CALCULATIONS
     ======================================================= */

  {
    id: "calculation",
    premium: false,

    label: {
      de: "Berechnungen",
      en: "Calculations",
    },

    eyebrow: {
      de: "Berechnungen",
      en: "Calculations",
    },

    features: [
      {
        id: "calculations",

        title: {
          de: "Finanzen einfach im Blick",
          en: "Your finances at a glance",
        },

        description: {
          de: "Budgets planen, Einnahmen und Ausgaben verwalten oder schnelle Berechnungen durchführen – oneNizer bringt deine wichtigsten Finanzfunktionen übersichtlich an einen Ort.",

          en: "Plan budgets, manage income and expenses or make quick calculations – oneNizer keeps your most important financial tools organized in one place.",
        },

        highlights: {
          de: [
            "Budgets planen & Ausgaben verfolgen",
            "Finanzen, Einnahmen & Ersparnisse verwalten",
            "Berechnungen durchführen & Ergebnisse speichern",
          ],

          en: [
            "Plan budgets & track expenses",
            "Manage finances, income & savings",
            "Calculate & save results",
          ],
        },

        visualType: "triple-device",

        images: [
          {
            src: {
              de: "/images/showcase/calculation/budget-en.jpg",
              en: "/images/showcase/calculation/budget-en.jpg",
            },

            fallback: "/images/showcase/calculation/budget.jpg",

            alt: {
              de: "oneNizer Budget Übersicht",
              en: "oneNizer Budget overview",
            },
          },

          {
            src: {
              de: "/images/showcase/calculation/finance-en.jpg",
              en: "/images/showcase/calculation/finance-en.jpg",
            },

            fallback: "/images/showcase/calculation/finance.jpg",

            alt: {
              de: "oneNizer Finanzen Übersicht",
              en: "oneNizer Finance overview",
            },
          },

          {
            src: {
              de: "/images/showcase/calculation/calculator-en.jpg",
              en: "/images/showcase/calculation/calculator-en.jpg",
            },

            fallback: "/images/showcase/calculation/calculator.jpg",

            alt: {
              de: "oneNizer Taschenrechner",
              en: "oneNizer Calculator",
            },
          },
        ],
      },
    ],
  },

  /* =======================================================
     PREMIUM
     ======================================================= */

  {
    id: "premium",
    premium: true,

    label: {
      de: "Premium",
      en: "Premium",
    },

    eyebrow: {
      de: "Premium",
      en: "Premium",
    },

    premiumInfo: {
      price: {
        de: "3,99 € pro Monat",
        en: "€3.99 per month",
      },

      description: {
        de: "Premium kostet 3,99 € pro Monat und kann jederzeit über den Apple App Store oder Google Play Store gekündigt werden. Für Premium-Funktionen zwischen zwei Nutzern benötigen beide ein aktives Premium-Abo.",

        en: "Premium costs €3.99 per month and can be canceled at any time through the Apple App Store or Google Play Store. Both users need an active Premium subscription to use Premium features with each other.",
      },
    },

    features: [
      {
        id: "tracking",

        tabLabel: {
          de: "Tracking",
          en: "Tracking",
        },

        title: {
          de: "Live-Standort gezielt teilen",
          en: "Share your live location",
        },

        description: {
          de: "Teile deinen Live-Standort gezielt mit ausgewählten Freunden oder Familienmitgliedern. Dein Standort kann nur von einem Freund gesehen werden, wenn du die Standortfreigabe für diese Person ausdrücklich aktiviert hast und ihr beide ein aktives Premium-Abo nutzt. Du entscheidest selbst, wie lange dein Standort geteilt wird: 15 Minuten, 1 Stunde, 8 Stunden oder bis auf Widerruf. Eine aktive Standortfreigabe kannst du jederzeit beenden.",

          en: "Share your live location with selected friends or family members. A friend can only see your location if you have explicitly enabled location sharing for that person and both of you have an active Premium subscription. You decide how long your location is shared: 15 minutes, 1 hour, 8 hours, or until you revoke access. You can stop active location sharing at any time.",
        },

        note: {
          de: "Standorte werden nur nach ausdrücklicher Freigabe geteilt. Beide Nutzer benötigen ein aktives Premium-Abo.",

          en: "Locations are only shared with your explicit permission. Both users need an active Premium subscription.",
        },

        visualType: "device",

        images: [
          {
            src: {
              de: "/images/showcase/premium/tracking-en.png",
              en: "/images/showcase/premium/tracking-en.png",
            },

            fallback: "/images/showcase/premium/tracking.png",

            alt: {
              de: "oneNizer Premium Live-Tracking",
              en: "oneNizer Premium live tracking",
            },
          },
        ],
      },

      {
        id: "chat",

        tabLabel: {
          de: "Chat",
          en: "Chat",
        },

        title: {
          de: "Direkt miteinander schreiben",
          en: "Chat directly with each other",
        },

        description: {
          de: "Bleibt direkt in oneNizer miteinander in Kontakt und stimmt euch dort ab, wo auch eure Planung und Organisation stattfindet. Damit zwei Nutzer miteinander chatten können, benötigen beide ein aktives Premium-Abo. Eine Kommunikation zwischen einem Premium- und einem Free-Nutzer ist nicht möglich. Alle Chat-Nachrichten und Anhänge werden nach 30 Tagen automatisch und endgültig gelöscht.",

          en: "Stay connected directly in oneNizer and keep your conversations where your planning and organization already take place. Both users need an active Premium subscription to chat with each other. Communication between a Premium user and a Free user is not available. All chat messages and attachments are automatically and permanently deleted after 30 days.",
        },

        note: {
          de: "Beide Nutzer benötigen Premium. Nachrichten und Anhänge werden nach 30 Tagen automatisch und endgültig gelöscht.",

          en: "Both users need Premium. Messages and attachments are automatically and permanently deleted after 30 days.",
        },

        visualType: "device",

        images: [
          {
            src: {
              de: "/images/showcase/premium/chat-en.png",
              en: "/images/showcase/premium/chat-en.png",
            },

            fallback: "/images/showcase/premium/chat.png",

            alt: {
              de: "oneNizer Premium Chat",
              en: "oneNizer Premium chat",
            },
          },
        ],
      },
    ],
  },
];

/* =========================================================
   FEATURE CAROUSEL
   ========================================================= */

function FeatureCarousel() {
  const { i18n } = useTranslation();

  const currentLang = i18n.language?.toLowerCase().startsWith("de")
    ? "de"
    : "en";

  const carouselRef = useRef(null);

  const slideRefs = useRef([]);

  const [activeSection, setActiveSection] = useState(0);

  const [featureIndexes, setFeatureIndexes] = useState(
    carouselSections.map(() => 0),
  );

  const [isFeatureChanging, setIsFeatureChanging] = useState(false);

  /* =======================================================
     AUTO-PLAY / PAUSE
     ======================================================= */

  const [isAutoPlayPaused, setIsAutoPlayPaused] = useState(false);
  const [isInteractionPaused, setIsInteractionPaused] = useState(false);

  const isCarouselPaused = isAutoPlayPaused || isInteractionPaused;

  /* =======================================================
     MAIN SECTION NAVIGATION
     ======================================================= */

  const scrollToSection = useCallback((index) => {
    const normalizedIndex =
      (index + carouselSections.length) % carouselSections.length;

    const carousel = carouselRef.current;

    const slide = slideRefs.current[normalizedIndex];

    if (!carousel || !slide) {
      return;
    }

    setFeatureIndexes((current) => {
      const copy = [...current];

      copy[normalizedIndex] = 0;

      return copy;
    });

    const targetLeft =
      slide.offsetLeft - (carousel.clientWidth - slide.offsetWidth) / 2;

    carousel.scrollTo({
      left: targetLeft,
      behavior: "smooth",
    });

    setActiveSection(normalizedIndex);
  }, []);

  const previousSection = () => {
    scrollToSection(activeSection - 1);
  };

  const nextSection = () => {
    scrollToSection(activeSection + 1);
  };

  /* =======================================================
     MANUAL FEATURE CHANGE
     ======================================================= */

  const changeFeature = (sectionIndex, featureIndex) => {
    if (featureIndexes[sectionIndex] === featureIndex || isFeatureChanging) {
      return;
    }

    setIsFeatureChanging(true);

    window.setTimeout(() => {
      setFeatureIndexes((current) => {
        const copy = [...current];

        copy[sectionIndex] = featureIndex;

        return copy;
      });

      window.setTimeout(() => {
        setIsFeatureChanging(false);
      }, 60);
    }, 180);
  };

  /* =======================================================
     AUTOMATIC FEATURE CHANGE
     ======================================================= */

  useEffect(() => {
    const section = carouselSections[activeSection];

    if (!section || section.features.length <= 1 || isCarouselPaused) {
      return undefined;
    }

    const interval = window.setInterval(() => {
      setIsFeatureChanging(true);

      window.setTimeout(() => {
        setFeatureIndexes((current) => {
          const copy = [...current];

          copy[activeSection] =
            (copy[activeSection] + 1) % section.features.length;

          return copy;
        });

        window.setTimeout(() => {
          setIsFeatureChanging(false);
        }, 60);
      }, 180);
    }, 4000);

    return () => {
      window.clearInterval(interval);
    };
  }, [activeSection, isCarouselPaused]);

  /* =======================================================
     AUTOMATIC SECTION CHANGE
     ======================================================= */

  useEffect(() => {
    if (isCarouselPaused) {
      return undefined;
    }

    const interval = window.setInterval(() => {
      scrollToSection(activeSection + 1);
    }, 8000);

    return () => {
      window.clearInterval(interval);
    };
  }, [activeSection, scrollToSection, isCarouselPaused]);

  /* =======================================================
     DETECT MANUAL SWIPE
     ======================================================= */

  useEffect(() => {
    const carousel = carouselRef.current;

    if (!carousel) {
      return undefined;
    }

    let timeout;

    const handleScroll = () => {
      window.clearTimeout(timeout);

      timeout = window.setTimeout(() => {
        const carouselRect = carousel.getBoundingClientRect();

        const carouselCenter = carouselRect.left + carouselRect.width / 2;

        let nearestIndex = 0;

        let nearestDistance = Infinity;

        slideRefs.current.forEach((slide, index) => {
          if (!slide) return;

          const rect = slide.getBoundingClientRect();

          const center = rect.left + rect.width / 2;

          const distance = Math.abs(carouselCenter - center);

          if (distance < nearestDistance) {
            nearestDistance = distance;

            nearestIndex = index;
          }
        });

        setActiveSection((currentSection) => {
          if (currentSection === nearestIndex) {
            return currentSection;
          }

          setFeatureIndexes((current) => {
            const copy = [...current];

            copy[nearestIndex] = 0;

            return copy;
          });

          return nearestIndex;
        });
      }, 100);
    };

    carousel.addEventListener("scroll", handleScroll, {
      passive: true,
    });

    return () => {
      window.clearTimeout(timeout);

      carousel.removeEventListener("scroll", handleScroll);
    };
  }, []);

  /* =======================================================
     RENDER
     ======================================================= */

  return (
    <section
      className="feature-carousel"
      onMouseEnter={() => setIsInteractionPaused(true)}
      onMouseLeave={() => setIsInteractionPaused(false)}
      onTouchStart={() => setIsInteractionPaused(true)}
      onTouchEnd={() => setIsInteractionPaused(false)}
      onTouchCancel={() => setIsInteractionPaused(false)}
    >
      {/* HEADER */}

      <div className="feature-carousel__header">
        <span className="feature-carousel__header-badge">
          {currentLang === "de" ? "oneNizer Funktionen" : "oneNizer Features"}
        </span>

        <h2>
          {currentLang === "de"
            ? "Alles, was euren Alltag einfacher macht."
            : "Everything that makes everyday life easier."}
        </h2>

        <p>
          {currentLang === "de"
            ? "Planung, gemeinsame Listen, Finanzen und Kommunikation – übersichtlich an einem Ort."
            : "Planning, shared lists, finances and communication – clearly organized in one place."}
        </p>
      </div>

      {/* MAIN NAVIGATION */}

      <div className="feature-carousel__navigation">
        {carouselSections.map((section, index) => (
          <button
            key={section.id}
            type="button"
            className={`feature-carousel__nav-item ${
              activeSection === index ? "active" : ""
            }`}
            onClick={() => scrollToSection(index)}
          >
            {section.premium && <span>👑 </span>}

            {section.label[currentLang]}
          </button>
        ))}
      </div>

      {/* CAROUSEL */}

      <div className="feature-carousel__stage">
        <button
          type="button"
          className="
            feature-carousel__arrow
            feature-carousel__arrow--left
          "
          onClick={previousSection}
          aria-label={
            currentLang === "de"
              ? "Vorherigen Bereich anzeigen"
              : "Show previous section"
          }
        >
          ‹
        </button>

        <div ref={carouselRef} className="feature-carousel__viewport">
          {carouselSections.map((section, sectionIndex) => {
            const featureIndex = featureIndexes[sectionIndex];

            const feature = section.features[featureIndex];

            const isActive = activeSection === sectionIndex;

            return (
              <article
                key={section.id}
                ref={(element) => {
                  slideRefs.current[sectionIndex] = element;
                }}
                className={`feature-carousel__slide ${
                  section.premium ? "feature-carousel__slide--premium" : ""
                } ${isActive ? "is-active" : ""}`}
              >
                <div
                  className={`feature-carousel__slide-content ${
                    isActive && isFeatureChanging ? "is-changing" : ""
                  }`}
                >
                  {/* TEXT */}

                  <div className="feature-carousel__text">
                    <span className="feature-carousel__eyebrow">
                      {section.premium && "👑 "}

                      {section.eyebrow[currentLang]}
                    </span>

                    <h3>{feature.title[currentLang]}</h3>

                    <p>{feature.description[currentLang]}</p>

                    {/* HIGHLIGHTS */}

                    {feature.highlights && (
                      <div className="feature-carousel__highlights">
                        {feature.highlights[currentLang].map(
                          (highlight, index) => (
                            <div
                              className="feature-carousel__highlight"
                              key={index}
                            >
                              <span className="feature-carousel__highlight-icon">
                                ✓
                              </span>

                              <span>{highlight}</span>
                            </div>
                          ),
                        )}
                      </div>
                    )}

                    {/* PREMIUM */}

                    {section.premium && (
                      <div className="feature-carousel__premium-notice">
                        <span className="feature-carousel__premium-icon">
                          ★
                        </span>

                        <div>
                          <strong>
                            {currentLang === "de"
                              ? "Premium-Funktion"
                              : "Premium feature"}
                          </strong>

                          <span>
                            {currentLang === "de"
                              ? "Tracking und Chat stehen Premium-Nutzern zur Verfügung."
                              : "Tracking and chat are available to Premium users."}
                          </span>
                        </div>
                      </div>
                    )}

                    {/* INNER NAVIGATION */}

                    {section.features.length > 1 && (
                      <>
                        <div className="feature-carousel__feature-tabs">
                          {section.features.map((tabFeature, tabIndex) => (
                            <button
                              key={tabFeature.id}
                              type="button"
                              className={`feature-carousel__feature-tab ${
                                featureIndex === tabIndex ? "active" : ""
                              }`}
                              onClick={() =>
                                changeFeature(sectionIndex, tabIndex)
                              }
                            >
                              {tabFeature.tabLabel[currentLang]}
                            </button>
                          ))}
                        </div>

                        <div className="feature-carousel__feature-progress">
                          {section.features.map((dotFeature, dotIndex) => (
                            <button
                              key={dotFeature.id}
                              type="button"
                              aria-label={`${
                                dotFeature.tabLabel[currentLang]
                              } ${currentLang === "de" ? "anzeigen" : "show"}`}
                              className={`feature-carousel__feature-dot ${
                                featureIndex === dotIndex ? "active" : ""
                              }`}
                              onClick={() =>
                                changeFeature(sectionIndex, dotIndex)
                              }
                            />
                          ))}
                        </div>
                      </>
                    )}
                  </div>

                  {/* VISUAL */}

                  <div className="feature-carousel__visual">
                    <div className="feature-carousel__visual-glow" />

                    <FeatureVisual
                      feature={feature}
                      currentLang={currentLang}
                    />
                  </div>
                </div>
              </article>
            );
          })}
        </div>

        <button
          type="button"
          className="
            feature-carousel__arrow
            feature-carousel__arrow--right
          "
          onClick={nextSection}
          aria-label={
            currentLang === "de"
              ? "Nächsten Bereich anzeigen"
              : "Show next section"
          }
        >
          ›
        </button>
      </div>

      {/* SECTION DOTS */}

      <div className="feature-carousel__section-dots">
        <button
          type="button"
          className={`feature-carousel__pause-button ${
            isAutoPlayPaused ? "is-paused" : ""
          }`}
          onClick={() => setIsAutoPlayPaused((paused) => !paused)}
          aria-label={
            isAutoPlayPaused
              ? currentLang === "de"
                ? "Automatischen Wechsel fortsetzen"
                : "Resume automatic rotation"
              : currentLang === "de"
                ? "Automatischen Wechsel pausieren"
                : "Pause automatic rotation"
          }
          title={
            isAutoPlayPaused
              ? currentLang === "de"
                ? "Fortsetzen"
                : "Resume"
              : currentLang === "de"
                ? "Pausieren"
                : "Pause"
          }
        >
          {isAutoPlayPaused ? "▶" : "Ⅱ"}
        </button>

        {carouselSections.map((section, index) => (
          <button
            key={section.id}
            type="button"
            aria-label={`${section.label[currentLang]} ${
              currentLang === "de" ? "anzeigen" : "show"
            }`}
            className={`feature-carousel__section-dot ${
              activeSection === index ? "active" : ""
            }`}
            onClick={() => scrollToSection(index)}
          />
        ))}
      </div>
    </section>
  );
}

/* =========================================================
   FEATURE VISUAL
   ========================================================= */

function FeatureVisual({ feature, currentLang }) {
  if (feature.visualType === "planning-flow") {
    return (
      <div className="feature-carousel__planning-flow">
        <Device
          image={feature.images[0]}
          currentLang={currentLang}
          className="
            feature-device--planning
            feature-device--planning-calendar
          "
        />

        <Device
          image={feature.images[1]}
          currentLang={currentLang}
          className="
            feature-device--planning
            feature-device--planning-schedule
          "
        />

        <Device
          image={feature.images[2]}
          currentLang={currentLang}
          className="
            feature-device--planning
            feature-device--planning-poll
          "
        />
      </div>
    );
  }

  if (feature.visualType === "lists-stack") {
    return (
      <div className="feature-carousel__lists-stack">
        <Device
          image={feature.images[0]}
          currentLang={currentLang}
          className="
            feature-device--lists
            feature-device--lists-main
          "
        />

        <Device
          image={feature.images[1]}
          currentLang={currentLang}
          className="
            feature-device--lists
            feature-device--lists-top
          "
        />

        <Device
          image={feature.images[2]}
          currentLang={currentLang}
          className="
            feature-device--lists
            feature-device--lists-bottom
          "
        />
      </div>
    );
  }

  if (feature.visualType === "triple-device") {
    return (
      <div className="feature-carousel__triple-phone">
        <Device
          image={feature.images[0]}
          currentLang={currentLang}
          className="
            feature-device--triple
            feature-device--triple-left
          "
        />

        <Device
          image={feature.images[1]}
          currentLang={currentLang}
          className="
            feature-device--triple
            feature-device--triple-center
          "
        />

        <Device
          image={feature.images[2]}
          currentLang={currentLang}
          className="
            feature-device--triple
            feature-device--triple-right
          "
        />
      </div>
    );
  }

  if (feature.visualType === "double-device") {
    return (
      <div className="feature-carousel__double-phone">
        <Device
          image={feature.images[0]}
          currentLang={currentLang}
          className="feature-device--front"
        />

        <Device
          image={feature.images[1]}
          currentLang={currentLang}
          className="feature-device--back"
        />
      </div>
    );
  }

  return (
    <Device
      image={feature.images[0]}
      currentLang={currentLang}
      className="feature-device--single"
    />
  );
}

/* =========================================================
   DEVICE
   ========================================================= */

function Device({ image, currentLang, className = "" }) {
  return (
    <div className={`feature-device ${className}`}>
      <span className="feature-device__button feature-device__button--silent" />

      <span className="feature-device__button feature-device__button--volume-up" />

      <span className="feature-device__button feature-device__button--volume-down" />

      <span className="feature-device__button feature-device__button--power" />

      <div className="feature-device__screen">
        <ResponsiveImage image={image} currentLang={currentLang} />
      </div>

      <div className="feature-device__island">
        <span className="feature-device__camera" />
      </div>
    </div>
  );
}

/* =========================================================
   IMAGE WITH AUTOMATIC FALLBACK
   ========================================================= */

function ResponsiveImage({ image, currentLang, className = "" }) {
  const localizedSrc = image.src?.[currentLang];

  const candidates = [localizedSrc, image.fallback].filter(Boolean);

  const [currentSourceIndex, setCurrentSourceIndex] = useState(0);

  useEffect(() => {
    setCurrentSourceIndex(0);
  }, [localizedSrc, image.fallback, currentLang]);

  const src = candidates[currentSourceIndex];

  const handleError = () => {
    if (currentSourceIndex < candidates.length - 1) {
      setCurrentSourceIndex((index) => index + 1);
    }
  };

  if (!src) {
    return null;
  }

  return (
    <img
      src={src}
      alt={image.alt?.[currentLang] || ""}
      className={className}
      loading="lazy"
      onError={handleError}
    />
  );
}

export default FeatureCarousel;
